import { AppointmentFormData, ClientFormData, ServiceTemplateFormData } from '../types/forms';

/**
 * Utility function to get only the changed fields between original and current data
 */
export function getChangedFields<T extends Record<string, any>>(
  original: T,
  current: T
): Partial<T> {
  const changes: Partial<T> = {};

  for (const key in current) {
    if (current.hasOwnProperty(key)) {
      const originalValue = original[key];
      const currentValue = current[key];

      // Handle arrays (like services)
      if (Array.isArray(originalValue) && Array.isArray(currentValue)) {
        if (!areArraysEqual(originalValue, currentValue)) {
          changes[key] = currentValue;
        }
      }
      // Handle primitive values
      else if (originalValue !== currentValue) {
        changes[key] = currentValue;
      }
    }
  }

  return changes;
}

/**
 * Compare two arrays for equality (deep comparison for objects)
 */
function areArraysEqual(arr1: any[], arr2: any[]): boolean {
  if (arr1.length !== arr2.length) {
    return false;
  }

  for (let i = 0; i < arr1.length; i++) {
    const item1 = arr1[i];
    const item2 = arr2[i];

    if (typeof item1 === 'object' && typeof item2 === 'object') {
      if (!areObjectsEqual(item1, item2)) {
        return false;
      }
    } else if (item1 !== item2) {
      return false;
    }
  }

  return true;
}

/**
 * Compare two objects for equality
 */
function areObjectsEqual(obj1: any, obj2: any): boolean {
  const keys1 = Object.keys(obj1);
  const keys2 = Object.keys(obj2);

  if (keys1.length !== keys2.length) {
    return false;
  }

  for (const key of keys1) {
    if (obj1[key] !== obj2[key]) {
      return false;
    }
  }

  return true;
}

/**
 * Specific function for appointment changes
 */
export function getAppointmentChanges(
  original: AppointmentFormData,
  current: AppointmentFormData
): Partial<AppointmentFormData> {
  return getChangedFields(original, current);
}

/**
 * Specific function for client changes
 */
export function getClientChanges(
  original: ClientFormData,
  current: ClientFormData
): Partial<ClientFormData> {
  return getChangedFields(original, current);
}

/**
 * Specific function for service template changes
 */
export function getServiceTemplateChanges(
  original: ServiceTemplateFormData,
  current: ServiceTemplateFormData
): Partial<ServiceTemplateFormData> {
  return getChangedFields(original, current);
}
